import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Clock, MessageSquare, Paperclip, ArrowLeft, Send, Filter, CheckCircle2, AlertCircle } from "lucide-react";

const STATUS_META = {
  open: { label: "Open", color: "#60a5fa" },
  in_progress: { label: "In progress", color: "#f5a623" },
  resolved: { label: "Resolved", color: "#22c55e" },
  closed: { label: "Closed", color: "#888888" },
};

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

export default function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [openTicket, setOpenTicket] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data || []);
    } catch (err) {
      toast.error("Could not load tickets");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const visible = filter === "all" ? tickets : tickets.filter((t) => t.status === filter);
  const counts = {
    all: tickets.length,
    open: tickets.filter((t) => t.status === "open").length,
    in_progress: tickets.filter((t) => t.status === "in_progress").length,
    resolved: tickets.filter((t) => t.status === "resolved").length,
    closed: tickets.filter((t) => t.status === "closed").length,
  };

  if (openTicket) {
    return <AdminTicketDetail ticketId={openTicket.id} onBack={() => { setOpenTicket(null); load(); }} />;
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="admin-support-page">
      <div className="flex items-center gap-3 mb-2">
        <div
          className="flex items-center justify-center rounded-2xl"
          style={{ width: 48, height: 48, background: "var(--lime-glow)", color: "var(--lime)", border: "1px solid var(--border-lime)" }}
        >
          <Inbox size={24} />
        </div>
        <div>
          <h1 className="h-display text-3xl sm:text-4xl">Support inbox</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            All student support tickets · {counts.open} open · {counts.in_progress} in progress
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-2 mt-6 mb-4 -mx-2 px-2" data-testid="admin-support-filter">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            data-testid={`admin-support-filter-${f.value}`}
            className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-full text-xs transition"
            style={{
              background: filter === f.value ? "var(--lime-glow)" : "var(--surface2)",
              border: `1px solid ${filter === f.value ? "var(--lime)" : "var(--border)"}`,
              color: filter === f.value ? "var(--lime)" : "var(--text)",
            }}
          >
            <span className="font-mono uppercase tracking-wider whitespace-nowrap">{f.label}</span>
            <span
              className="font-mono text-[10px] px-1.5 py-0.5 rounded-full"
              style={{
                background: filter === f.value ? "var(--lime)" : "var(--surface3)",
                color: filter === f.value ? "#0c0c0c" : "var(--text-muted)",
                minWidth: 18, textAlign: "center",
              }}
            >
              {counts[f.value] || 0}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><span className="spinner" /></div>
      ) : visible.length === 0 ? (
        <div className="surface-card p-10 text-center" data-testid="admin-support-empty">
          <CheckCircle2 size={32} style={{ color: "var(--success)" }} className="mx-auto mb-3" />
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            No {filter === "all" ? "" : filter.replace("_", " ")} tickets. Inbox zero.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-support-list">
          {visible.map((t) => {
            const meta = STATUS_META[t.status] || STATUS_META.open;
            return (
              <button
                key={t.id}
                onClick={() => setOpenTicket(t)}
                className="w-full text-left p-4 rounded-xl flex items-start gap-3 transition hover:opacity-90"
                style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
                data-testid={`admin-ticket-${t.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate">{t.subject}</span>
                    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0" style={{ background: `${meta.color}22`, color: meta.color }}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                    From <strong style={{ color: "var(--text)" }}>{t.user_name || t.user_email}</strong>
                  </div>
                  <div className="flex items-center gap-3 mt-1 flex-wrap text-[11px]" style={{ color: "var(--text-muted)" }}>
                    <span className="font-mono uppercase tracking-wider">{t.priority}</span>
                    <span className="flex items-center gap-1"><Clock size={10} /> {new Date(t.updated_at).toLocaleDateString()}</span>
                    {t.messages?.length > 0 && (
                      <span className="flex items-center gap-1"><MessageSquare size={10} /> {t.messages.length}</span>
                    )}
                    {t.attachments?.length > 0 && (
                      <span className="flex items-center gap-1"><Paperclip size={10} /> {t.attachments.length}</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminTicketDetail({ ticketId, onBack }) {
  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/support/tickets/${ticketId}`);
      setTicket(data);
    } catch {
      toast.error("Could not load ticket");
    }
  };

  // eslint-disable-next-line
  useEffect(() => { load(); }, [ticketId]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/support/tickets/${ticketId}/reply`, { body: reply.trim() });
      setTicket(data);
      setReply("");
      toast.success("Reply sent");
    } catch {
      toast.error("Could not send reply");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (newStatus) => {
    setStatusBusy(true);
    try {
      const { data } = await api.patch(`/support/tickets/${ticketId}/status`, { status: newStatus });
      setTicket(data);
      toast.success(`Marked ${STATUS_META[newStatus].label}`);
    } catch {
      toast.error("Could not update status");
    } finally {
      setStatusBusy(false);
    }
  };

  if (!ticket) return <div className="surface-card p-6 text-center text-sm" style={{ color: "var(--text-muted)" }}>Loading…</div>;
  const meta = STATUS_META[ticket.status] || STATUS_META.open;

  return (
    <div className="max-w-3xl mx-auto" data-testid="admin-ticket-detail">
      <button onClick={onBack} className="flex items-center gap-1.5 mb-3 text-sm transition hover:opacity-80" style={{ color: "var(--text-muted)" }} data-testid="admin-ticket-back">
        <ArrowLeft size={14} /> All tickets
      </button>

      <div className="surface-card p-6 mb-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: `${meta.color}22`, color: meta.color }}>{meta.label}</span>
            <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: "var(--surface2)", color: "var(--text-muted)" }}>{ticket.priority}</span>
          </div>
          <div className="flex items-center gap-1" data-testid="admin-status-controls">
            {Object.entries(STATUS_META).filter(([s]) => s !== ticket.status).map(([s, m]) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                disabled={statusBusy}
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded transition hover:opacity-80"
                style={{ background: "var(--surface2)", border: "1px solid var(--border)", color: m.color }}
                data-testid={`admin-set-status-${s}`}
              >
                {s === "in_progress" ? "Pick up" : `Mark ${m.label}`}
              </button>
            ))}
          </div>
        </div>
        <h2 className="h-display text-xl mb-2">{ticket.subject}</h2>
        <p className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
          From <strong style={{ color: "var(--text)" }}>{ticket.user_name || ticket.user_email}</strong> · {new Date(ticket.created_at).toLocaleString()}
        </p>
        <p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text)" }}>{ticket.description}</p>
        {ticket.attachments?.length > 0 && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {ticket.attachments.map((a) => (
              <span key={a.file_id} className="px-3 py-1.5 rounded-xl text-xs flex items-center gap-1.5" style={{ background: "var(--surface2)", border: "1px solid var(--border)" }}>
                <Paperclip size={11} /> {a.filename}
              </span>
            ))}
          </div>
        )}
      </div>

      {ticket.messages?.length > 0 && (
        <div className="space-y-3 mb-4">
          {ticket.messages.map((m) => (
            <div
              key={m.id}
              className="surface-card p-4"
            >
              <div className="flex items-center gap-2 mb-2 text-[11px]" style={{ color: "var(--text-muted)" }}>
                <span className="font-semibold" style={{ color: m.author_is_admin ? "var(--lime)" : "var(--text)" }}>
                  {m.author_is_admin ? "Coach" : (m.author_name || "Student")}
                </span>
                <span className="font-mono">{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{m.body}</p>
            </div>
          ))}
        </div>
      )}

      {ticket.status !== "closed" && (
        <div className="surface-card p-4">
          <textarea
            rows={3}
            className="input-base"
            placeholder="Reply to the student…"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            data-testid="admin-reply-input"
          />
          <div className="mt-3 flex justify-end">
            <button onClick={sendReply} disabled={busy || !reply.trim()} className="btn-primary text-sm flex items-center gap-2" data-testid="admin-reply-button">
              {busy ? <span className="spinner" /> : <Send size={13} />} Send reply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
